import {
  type ApolloCache,
  type FieldReadFunction,
  type Reference,
  type StoreObject,
  gql,
} from "@apollo/client";
import type {
  ThreadFragment,
  ThreadWithMessagesQuery,
} from "@/__generated__/graphql";
import { removeNodesFromConnections } from "@/app/_/subscriptions/writeObjectChangeToCache";
import { Queries } from "@/constants";
import { State } from "@/state";
import {
  type StreamingContent,
  messageIsWorkflowStep,
} from "@/components/organisms/ToolCallCard/ToolCallCard.tools";
import type {
  MessageEdge,
  MessageItem,
  ThreadMessagesConnection,
} from "./Thread.types";

// ─── Pending Change Cache ────────────────────────────────────────────────────

type PendingChangeCacheWrite = {
  __typename: "PendingChangeObject";
  identifier: string;
  changeType: string;
  modelName: string;
  status: string;
  objectIdentifier: string;
};

type PendingChangeInput = {
  identifier: string;
  changeType: string;
  modelName?: string | null;
  status?: string | null;
  objectIdentifier?: string | null;
};

export function toPendingChangeCacheWrite(
  input: PendingChangeInput,
): PendingChangeCacheWrite {
  return {
    __typename: "PendingChangeObject",
    identifier: input.identifier,
    changeType: input.changeType,
    modelName: input.modelName ?? "unknown",
    status: input.status ?? "pending",
    objectIdentifier: input.objectIdentifier ?? "",
  };
}

// ─── Thread Message Connection ───────────────────────────────────────────────

type MessageEdgeLike = {
  node?: Reference | StoreObject | null;
  cursor?: string | null;
  __typename?: string;
};

type MessageConnectionLike = {
  __typename?: string;
  edges?: readonly MessageEdgeLike[] | null;
  pageInfo?: unknown;
};

/**
 * Union two pages of message edges, newest last.
 *
 * Both the Apollo field policy and the load-older path want the same thing:
 * one edge per message, ordered by ``createdAt``. ``incoming`` wins a key
 * collision, since it is always the fresher read of that row.
 */
export function mergeThreadMessageEdges(
  existing: MessageConnectionLike | null | undefined,
  incoming: MessageConnectionLike,
  readField: FieldReadFunction,
): MessageConnectionLike {
  const read = readField as unknown as (
    fieldName: string,
    from: Reference | StoreObject,
  ) => unknown;
  const at = (edge: MessageEdgeLike) =>
    edge.node ? ((read("createdAt", edge.node) as string) ?? "") : "";

  const byKey = new Map<string, MessageEdgeLike>();
  for (const edge of [...(existing?.edges ?? []), ...(incoming.edges ?? [])]) {
    const key = edge.node
      ? ((read("id", edge.node) ?? read("identifier", edge.node)) as
          | string
          | undefined)
      : undefined;
    if (key) byKey.set(key, edge);
  }

  return {
    __typename:
      incoming.__typename ??
      existing?.__typename ??
      "MessageObjectORMConnection",
    edges: [...byKey.values()].sort((a, b) => at(a).localeCompare(at(b))),
    pageInfo: incoming.pageInfo ?? existing?.pageInfo,
  };
}

// ─── Thread Message Content Cache ────────────────────────────────────────────

function relayGlobalId(typename: string, identifier: string): string {
  return btoa(`${typename}:${identifier}`);
}

/** ``[typename, identifier]`` from a relay global id, or nulls if it is not one. */
function relayParts(id: string): [string | null, string | null] {
  try {
    const [typename, identifier] = atob(id).split(":");
    return [typename ?? null, identifier ?? null];
  } catch {
    return [null, null];
  }
}

export type StreamingThreadMessageContentWrite = {
  __typename: "MessageContentObject";
  id: string;
  identifier: string;
  createdAt: string | null;
  order: number;
  type: string | null;
  status: string | null;
  text: string | null;
  toolName: string | null;
  toolInput: Record<string, unknown> | null;
  toolOutput: Record<string, unknown> | null;
  toolError: string | null;
  parentIdentifier: string | null;
  executionMode: string | null;
  iterationIndex: number | null;
  pendingChange: PendingChangeCacheWrite | null;
  nodes: Array<{ id: string; identifier?: string | null }>;
  context: Array<{
    __typename?: "MessageContextObject";
    identifier?: string;
    id?: string;
    node?: unknown;
  }>;
  execution: {
    identifier: string;
    source?: string | null;
    status?: string | null;
  } | null;
};

/**
 * The shape every streamed row is written in.
 *
 * It must cover everything ``ThreadMessage`` selects per content row, or a row
 * the stream wrote reads back incomplete and the panel query — which runs
 * cache-only mid-stream — has nothing to render. Add a field there, add it
 * here.
 */
const STREAMING_THREAD_MESSAGE_CONTENT_FRAGMENT = gql`
  fragment StreamingThreadMessageContent on MessageContentObject {
    __typename
    id
    identifier
    createdAt
    order
    type
    status
    text
    toolName
    toolInput
    toolOutput
    toolError
    parentIdentifier
    executionMode
    iterationIndex
    pendingChange {
      identifier
      changeType
      modelName
      status
      objectIdentifier
    }
    nodes {
      id
      identifier
    }
    context {
      id
      identifier
    }
    execution {
      identifier
      source
      status
    }
  }
`;

type ContentRowInput = {
  identifier?: string;
  id?: string;
  createdAt?: string | null;
  order?: number | null;
  streamingAgentText?: string | null;
  text?: string | null;
  type?: string | null;
  status?: string | null;
  toolName?: string | null;
  toolInput?: unknown;
  toolOutput?: unknown;
  toolError?: string | null;
  parentIdentifier?: string | null;
  executionMode?: string | null;
  iterationIndex?: number | null;
  pendingChange?: PendingChangeInput | null;
  nodes?: StreamingThreadMessageContentWrite["nodes"];
  context?: StreamingThreadMessageContentWrite["context"];
  execution?: StreamingThreadMessageContentWrite["execution"];
};

const MESSAGE_TYPENAME = "MessageObject";
const THREAD_MESSAGE_CONTENT_TYPENAME = "MessageContentObject";
const THREAD_MESSAGE_CONTEXT_TYPENAME = "MessageContextObject";

export function resolveThreadMessageContentCacheIds(row: {
  id?: string | null;
  identifier?: string | null;
}): { id: string; identifier: string } {
  const [typename, encoded] = row.id ? relayParts(row.id) : [null, null];
  const identifier = row.identifier ?? encoded ?? row.id;
  if (!identifier) {
    throw new Error(
      "ThreadMessageContent cache write requires id or identifier",
    );
  }
  return {
    id:
      row.id && typename === THREAD_MESSAGE_CONTENT_TYPENAME
        ? row.id
        : relayGlobalId(THREAD_MESSAGE_CONTENT_TYPENAME, identifier),
    identifier,
  };
}

function resolveContextCacheWrite(ctx: {
  identifier?: string;
  node?: unknown;
}): StreamingThreadMessageContentWrite["context"][number] | null {
  const identifier = ctx.identifier;
  if (!identifier) {
    return null;
  }
  return {
    __typename: THREAD_MESSAGE_CONTEXT_TYPENAME,
    id: relayGlobalId(THREAD_MESSAGE_CONTEXT_TYPENAME, identifier),
    identifier,
    node: ctx.node,
  };
}

export function toStreamingContentCacheWrite(
  row: ContentRowInput,
): StreamingThreadMessageContentWrite {
  const { id, identifier } = resolveThreadMessageContentCacheIds(row);

  return {
    __typename: THREAD_MESSAGE_CONTENT_TYPENAME,
    id,
    identifier,
    createdAt: row.createdAt ?? null,
    order: row.order ?? 0,
    text: row.streamingAgentText ?? row.text ?? null,
    type: row.type ?? null,
    status: row.status ?? null,
    toolName: row.toolName ?? null,
    toolInput: (row.toolInput as Record<string, unknown> | null) ?? null,
    toolOutput: (row.toolOutput as Record<string, unknown> | null) ?? null,
    toolError: row.toolError ?? null,
    parentIdentifier: row.parentIdentifier ?? null,
    executionMode: row.executionMode ?? null,
    iterationIndex: row.iterationIndex ?? null,
    pendingChange: row.pendingChange
      ? toPendingChangeCacheWrite(row.pendingChange)
      : null,
    nodes: row.nodes ?? [],
    context: row.context ?? [],
    execution: row.execution ?? null,
  };
}

function streamingChunkToContentCacheWrite(
  chunk: StreamingContent,
): StreamingThreadMessageContentWrite {
  return toStreamingContentCacheWrite({
    ...chunk,
    id: chunk.identifier,
    context: (chunk.context ?? [])
      .map(resolveContextCacheWrite)
      .filter((ctx): ctx is NonNullable<typeof ctx> => ctx != null),
  });
}

/**
 * Fold an incoming write onto the row already cached.
 *
 * Each publish carries the row's complete state, so incoming wins outright —
 * except for the two fields the stream never sends. ``nodes`` and
 * ``execution`` only ever arrive on a query or the finished turn, and must
 * survive the chunks in between.
 */
function mergeStreamingContentWrite(
  row: StreamingThreadMessageContentWrite,
  existingEntry: Reference | StoreObject,
  readField: (fieldName: string, from?: Reference | StoreObject) => unknown,
): StreamingThreadMessageContentWrite {
  return {
    ...row,
    nodes: row.nodes.length
      ? row.nodes
      : ((readField("nodes", existingEntry) as
          | StreamingThreadMessageContentWrite["nodes"]
          | undefined) ?? []),
    execution:
      row.execution ??
      (readField("execution", existingEntry) as
        | StreamingThreadMessageContentWrite["execution"]
        | undefined) ??
      null,
  };
}

function writeStreamingContentRow(
  cache: ApolloCache,
  data: StreamingThreadMessageContentWrite,
): Reference | StoreObject | undefined {
  return cache.writeFragment({
    fragment: STREAMING_THREAD_MESSAGE_CONTENT_FRAGMENT,
    fragmentName: "StreamingThreadMessageContent",
    data,
  });
}

// ─── Pin Segments ────────────────────────────────────────────────────────────

export function isPinSegmentHeader(message: MessageItem): boolean {
  return message.account != null || messageIsWorkflowStep(message);
}

type PinSegmentEdge = {
  edge: MessageEdge;
  index: number;
};

type PinSegment = {
  pinHeaderIndex: number | null;
  edges: PinSegmentEdge[];
};

export function buildPinSegments(visibleEdges: MessageEdge[]): PinSegment[] {
  const segments: PinSegment[] = [];
  let current: PinSegment | null = null;

  for (let index = 0; index < visibleEdges.length; index++) {
    const edge = visibleEdges[index];
    const entry = { edge, index };

    if (isPinSegmentHeader(edge.node)) {
      if (current) segments.push(current);
      current = { pinHeaderIndex: index, edges: [entry] };
    } else if (current) {
      current.edges.push(entry);
    } else {
      current = { pinHeaderIndex: null, edges: [entry] };
    }
  }

  if (current) segments.push(current);
  return segments;
}

// ─── GraphQL Queries & Fragments ─────────────────────────────────────────────
// The thread panel's queries (``THREAD_WITH_MESSAGES_QUERY``,
// ``THREAD_LOAD_MORE_MESSAGES_QUERY``) and the ``ThreadMessage`` fragment live in
// ``constants/queries.tsx``; the cache fragments below mirror that shape.

export const MESSAGES_PER_PAGE = 30;

// ─── Display Helpers ─────────────────────────────────────────────────────────

export function hasDisplayableContent(message: MessageItem): boolean {
  return (message.content ?? []).some(
    (row) =>
      !!row.text?.trim() ||
      !!row.toolName ||
      row.toolOutput != null ||
      !!row.toolError,
  );
}

// ─── Agent Turn ──────────────────────────────────────────────────────────────

export type AgentTurnPhase =
  | "idle"
  | "sending"
  | "send-failed"
  | "working"
  | "stalled"
  | "failed"
  | "empty";

const TURN_IN_PROGRESS_STATUSES = new Set(["queued", "running"]);

/**
 * Whether a turn is still being worked on.
 *
 * Liveness belongs to the turn, not to its rows: a run the user stopped goes
 * on writing for a second or two, and rows it already opened stay ``running``
 * in the server's copy until it finishes. Asked row by row, every card draws
 * itself as live through all of that — and a row left open by a run that died
 * draws itself as live for ever.
 */
export function turnIsLive(message: MessageItem, stopped: boolean): boolean {
  if (stopped) return false;
  const status = message.execution?.status ?? null;
  return status == null || TURN_IN_PROGRESS_STATUSES.has(status);
}

/** The agent message answering the latest turn, or null if the thread ends on the user. */
export function latestAgentReply(
  messages: readonly MessageItem[],
): MessageItem | null {
  const last = messages[messages.length - 1];
  return last && last.account == null ? last : null;
}

/**
 * Where the thread's latest turn stands, from what the server has saved of it.
 *
 * Whether a reply is still coming is read off the run's own status on the
 * agent message, never inferred from which stream events arrived: the next
 * read of the thread carries the run's terminal status however the stream
 * fared, so a finished turn cannot stay looking unfinished.
 *
 * A reply whose run the client has not read yet is still coming while it has
 * no rows or a row is still streaming: in prod the run starts on a Pub/Sub
 * push seconds after the send, so the placeholder is cached without it and
 * the stream's first row lands before any read brings it back. Only a reply
 * with no run whose rows are all done predates runs and is simply done.
 */
export function deriveAgentTurn(
  messages: readonly MessageItem[],
  client: {
    sending: boolean;
    sendFailed: boolean;
    /** Sent, and the server's copy of the turn has not reached the cache yet. */
    awaitingEcho: boolean;
  },
): AgentTurnPhase {
  if (client.sendFailed) return "send-failed";
  if (client.sending) return "sending";

  const reply = latestAgentReply(messages);
  const status = reply?.execution?.status ?? null;
  const hasReply = reply != null && hasDisplayableContent(reply);
  const rowStreaming = (reply?.content ?? []).some(
    (row) => row.status === "running",
  );
  const inProgress =
    client.awaitingEcho ||
    (reply != null &&
      (status == null
        ? !hasReply || rowStreaming
        : TURN_IN_PROGRESS_STATUSES.has(status)));

  // Whether a turn in progress has stalled is the reconciler's call.
  if (inProgress) return "working";
  if (reply == null) return "idle";
  if (status === "failed") return "failed";
  return hasReply ? "idle" : "empty";
}

const MESSAGE_EXISTS_FRAGMENT = gql`
  fragment MessageExists on MessageObject {
    id
  }
`;

/**
 * The cache key for a message already on this thread, or undefined.
 *
 * Nothing here creates a message: every row in the list is one the server put
 * there, in the server's own order. A chunk naming a message the client has
 * not seen yet is skipped — ``streamingAgentText`` and the tool fields are
 * cumulative, so the next chunk carries everything the skipped one did.
 */
function messageOnThread(
  cache: ApolloCache,
  messageIdentifier: string,
): string | undefined {
  const key = cache.identify({
    __typename: MESSAGE_TYPENAME,
    id: relayGlobalId(MESSAGE_TYPENAME, messageIdentifier),
  });
  if (!key) return undefined;
  const exists = cache.readFragment<{ id: string }>({
    id: key,
    fragment: MESSAGE_EXISTS_FRAGMENT,
    fragmentName: "MessageExists",
  });
  return exists ? key : undefined;
}

/** Apply one streamed content row to the message the stream named. */
export function writeStreamingContent(
  cache: ApolloCache,
  messageIdentifier: string,
  chunk: StreamingContent,
): void {
  const messageKey = messageOnThread(cache, messageIdentifier);
  if (!messageKey) return;

  cache.modify({
    id: messageKey,
    fields: {
      content(
        existing: readonly (Reference | StoreObject)[] = [],
        { readField },
      ) {
        let rowData = streamingChunkToContentCacheWrite(chunk);
        const index = existing.findIndex((entry) => {
          const id =
            readField<string>("identifier", entry as Reference | StoreObject) ??
            readField<string>("id", entry as Reference | StoreObject);
          return id === chunk.identifier;
        });
        if (index >= 0) {
          rowData = mergeStreamingContentWrite(
            rowData,
            existing[index] as Reference | StoreObject,
            readField,
          );
        }

        const rowRef = writeStreamingContentRow(cache, rowData);
        if (!rowRef) return existing;
        if (index < 0) return [...existing, rowRef];
        const next = existing.slice();
        next[index] = rowRef;
        return next;
      },
    },
  });
}

/**
 * Write the finished turn the server sent with ``done``.
 *
 * Its rows share identifiers with everything streamed onto them, so this
 * updates those cache entries in place rather than swapping rows, and the
 * connection merge folds in the edge if the list has not caught up yet.
 */
export function writePersistedMessage(
  cache: ApolloCache,
  threadNodeId: string,
  message: MessageItem,
): void {
  const { id, identifier } = message;
  if (!id || !identifier) return;

  const ref = cache.writeFragment({
    fragment: THREAD_MESSAGE_CACHE_FRAGMENT,
    fragmentName: "ThreadMessageCache",
    data: {
      __typename: MESSAGE_TYPENAME,
      id,
      identifier,
      createdAt: message.createdAt,
      account: message.account ?? null,
      context: message.context ?? [],
      // The finished turn is the agent's, which is sent about nothing.
      references: [],
      files: [],
      execution:
        (message as MessageItem & { execution?: unknown }).execution ?? null,
      timeSeriesData: message.timeSeriesData ?? null,
      // Rows go in as data for ``writeFragment`` to normalise. A cache
      // reference here is not read as a link: it is written as an empty row,
      // and the finished turn replaced its own streamed reply with blanks.
      content: (message.content ?? []).map((row) =>
        toStreamingContentCacheWrite(row as ContentRowInput),
      ),
    },
  });
  if (!ref) return;

  mergeUpdateNodeMessagesIntoCache(cache, threadNodeId, {
    edges: [
      {
        __typename: "MessageObjectEdge",
        cursor: `cursor-${identifier}`,
        node: ref,
      },
    ],
  });
}

/**
 * Point the thread at a pending session, or clear it.
 *
 * An identifier is only adopted when no session is already pending: the
 * stream announces the session on every chunk, and the one already cached is
 * the fuller record of it.
 */
export function setThreadPendingSession(
  cache: ApolloCache,
  threadId: string,
  sessionIdentifier: string | null,
): void {
  const threadKey = cache.identify({
    __typename: "ThreadObject",
    id: threadId,
  });
  if (!threadKey) return;

  cache.modify({
    id: threadKey,
    fields: {
      pendingSession(existing, { readField }) {
        if (sessionIdentifier == null) return null;
        if (existing && readField<string>("status", existing) === "pending") {
          return existing;
        }
        return {
          __typename: "PendingSessionObject",
          identifier: sessionIdentifier,
          status: "pending",
          changeCount: 0,
          pendingChangeCount: 0,
          hasPendingChanges: true,
          changes: [],
        };
      },
    },
  });
}

export function mergeUpdateNodeMessagesIntoCache(
  cache: ApolloCache,
  threadId: string,
  incoming: Parameters<typeof mergeThreadMessageEdges>[1],
): void {
  const cacheId = cache.identify({ __typename: "ThreadObject", id: threadId });
  if (!cacheId) return;
  cache.modify({
    id: cacheId,
    fields: {
      messages(
        existing: Parameters<typeof mergeThreadMessageEdges>[0],
        { readField }: { readField: FieldReadFunction },
      ) {
        return mergeThreadMessageEdges(existing, incoming, readField);
      },
    },
  });
}

const THREAD_MESSAGE_CACHE_FRAGMENT = gql`
  fragment ThreadMessageCache on MessageObject {
    __typename
    id
    identifier
    createdAt
    account {
      __typename
      id
      identifier
    }
    content {
      ...StreamingThreadMessageContent
    }
    context {
      id
      identifier
    }
    references {
      id
      identifier
    }
    files {
      id
      identifier
      name
    }
    execution {
      identifier
      source
      status
    }
    timeSeriesData {
      title
    }
  }
  ${STREAMING_THREAD_MESSAGE_CONTENT_FRAGMENT}
`;

/**
 * Seed the panel query for a thread the server just created.
 *
 * ``createNode`` returns the base Thread fragment only, so the message list
 * starts empty; the turn that created it is appended by
 * ``appendOptimisticUserTurn`` and the agent's rows arrive on the stream.
 */
export function seedThreadMessagesQueryCache(
  cache: ApolloCache,
  thread: ThreadFragment,
): void {
  cache.writeQuery({
    query: Queries.THREAD_WITH_MESSAGES_QUERY,
    variables: { id: thread.id, last: MESSAGES_PER_PAGE },
    data: {
      node: {
        __typename: "ThreadObject",
        id: thread.id,
        identifier: thread.identifier ?? thread.id,
        name: thread.name,
        description: thread.description ?? null,
        createdAt: thread.createdAt,
        updatedAt: thread.updatedAt,
        isRead: thread.isRead,
        isArchived: thread.isArchived,
        source: thread.source,
        messages: {
          __typename: "MessageObjectORMConnection" as const,
          edges: [],
          pageInfo: { hasPreviousPage: false, startCursor: null },
        },
        pendingSession: thread.pendingSession
          ? { ...thread.pendingSession, changes: [] }
          : null,
      },
    } as ThreadWithMessagesQuery,
    broadcast: true,
  });
}

// ─── Thread list cache ───────────────────────────────────────────────────────

/** Drop deleted thread rows from every cached ``threadsConnection`` page. */
export function removeThreadsFromConnections(
  cache: ApolloCache,
  threadIds: Iterable<string>,
) {
  const ids = [...threadIds];
  removeNodesFromConnections(cache, "threadsConnection", ids, "ThreadObject");
  State.getState().closeThreadTabs(ids);
}

export const THREAD_TAB_STATUS_FRAGMENT = gql`
  fragment ThreadTabStatus on ThreadObject {
    id
    pendingSession {
      status
      changeCount
      hasPendingChanges
    }
  }
`;

export function pendingDeltaCountFromSession(
  pendingSession:
    | {
        status?: string | null;
        changeCount?: number | null;
        hasPendingChanges?: boolean | null;
      }
    | null
    | undefined,
): number {
  if (
    pendingSession?.status !== "pending" ||
    pendingSession.hasPendingChanges === false
  ) {
    return 0;
  }
  return pendingSession.changeCount ?? 0;
}
